import { useSearchParams } from "react-router-dom";
import { Box } from "@mui/material";
import type { PremiseConfig } from "@shared/types/foyer";
import { SCHEME_MANIFESTS } from "@shared/schemes/manifests";
import type { TemplateSubSection } from "../../components/PremiseSidebar";
import LivePreview from "../../components/template/LivePreview";
import TemplateSubEditor, {
  useTemplateApps,
} from "../../components/template/TemplateSubEditor";
import {
  PanelHeading,
  SectionList,
  StartNavLayout,
} from "../../components/startnav/StartNavLayout";
import { START_NAV_ROOT } from "./HomeScreenPage";

/** What "Appearance" holds, in the order a place is set up: choose the
 *  template, then dress it. */
const APPEARANCE_ITEMS: {
  key: TemplateSubSection;
  label: string;
  subtitle: string;
}[] = [
  {
    key: "picker",
    label: "Pick template",
    subtitle: "The design every screen at this place is built from.",
  },
  {
    key: "design",
    label: "Colours",
    subtitle: "The template's colour palette.",
  },
  {
    key: "typography",
    label: "Typography",
    subtitle: "Fonts and text sizes.",
  },
  {
    key: "background",
    label: "Background & photos",
    subtitle: "Background images and the photos the template shows.",
  },
  {
    key: "branding",
    label: "Branding & logo",
    subtitle: "Your logo and name on the screen.",
  },
  {
    key: "icons",
    label: "Home cards",
    subtitle: "Icons and text on the home screen's cards.",
  },
];

/** Keys valid in `?item=` for this page - exported for the legacy redirect. */
export const APPEARANCE_KEYS = new Set<string>(
  APPEARANCE_ITEMS.map((i) => i.key)
);

/**
 * Start & navigation → Appearance ("Utseende"): the template choice and
 * everything about how it looks, as opposed to what it contains.
 */
export default function AppearancePage({
  premise,
  onChange,
}: {
  premise: PremiseConfig;
  onChange: (next: PremiseConfig) => void;
}) {
  const [params, setParams] = useSearchParams();
  const apps = useTemplateApps(premise);
  const manifest = premise.scheme
    ? SCHEME_MANIFESTS[premise.scheme.key]
    : undefined;
  // Home cards are the care template's; nothing else draws them.
  const isCareTemplate = manifest?.screens.some((s) => s.id === "tv") ?? false;
  const items = APPEARANCE_ITEMS.filter(
    (i) => i.key !== "icons" || isCareTemplate
  )
    // Nothing to style until there is a template to style.
    .filter((i) => i.key === "picker" || !!premise.scheme);

  const requested = params.get("item") ?? "";
  const current = items.find((i) => i.key === requested) ?? items[0]!;

  return (
    <StartNavLayout
      crumbs={[START_NAV_ROOT, { label: "Appearance" }]}
      pageTitle="Appearance"
      pageSubtitle="Choose the template and how it looks on every screen at this place."
      title={current.label}
      subtitle={current.subtitle}
      list={
        <SectionList
          groups={[
            {
              key: "appearance",
              title: "Appearance",
              subtitle: "Choose what to change.",
              items: items.map((i) => ({ key: i.key, label: i.label })),
            },
          ]}
          selected={current.key}
          onSelect={(key) =>
            setParams(
              (prev) => {
                const q = new URLSearchParams(prev);
                q.set("item", key);
                return q;
              },
              { replace: true }
            )
          }
        />
      }
      editor={
        <Box data-testid={`startnav-editor-${current.key}`}>
          <TemplateSubEditor
            premise={premise}
            sub={current.key}
            onChange={onChange}
            apps={apps}
            stateBar={false}
          />
        </Box>
      }
      preview={
        <Box>
          <PanelHeading
            title="Preview"
            subtitle="How the home screen looks with these settings."
          />
          <LivePreview premise={premise} maxWidth={440} hideLabel />
        </Box>
      }
    />
  );
}
